import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { syncGitHubCollaborators, inviteUserToGitHubOrg, bulkGrantRepoAccess } from '../services/api';
import { formatAccessExpiry } from '../utils/formatAccessExpiry';
import { PagedSlice } from '../components/Pagination';
import './UserManagement.css';

// Generate random password for CSV import
const generateRandomPassword = () => {
  const array = new Uint8Array(12);
  window.crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const withBreaks = (value) => {
  const text = value == null || value === '' ? '-' : String(value);
  const parts = text.split(/([/_\-.@])/);
  return parts.map((part, index) => (
    index % 2 === 1
      ? <React.Fragment key={index}>{part}<wbr /></React.Fragment>
      : <React.Fragment key={index}>{part}</React.Fragment>
  ));
};

const UserManagement = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showResetPasswordForm, setShowResetPasswordForm] = useState(null);
  const [showRepoAccess, setShowRepoAccess] = useState(null);
  const [showSetGitHubUsername, setShowSetGitHubUsername] = useState(null);
  const [userRepos, setUserRepos] = useState({});
  const [allRepos, setAllRepos] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    displayName: '',
    role: 'user',
    githubUsername: '',
    mustChangePassword: true,
    inviteToGitHubOrg: true,
    generatePassword: true
  });
  const [resetPasswordData, setResetPasswordData] = useState({ newPassword: '' });
  const [githubUsernameData, setGithubUsernameData] = useState({ githubUsername: '' });
  const [githubSuggestions, setGithubSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncStats, setSyncStats] = useState(null);
  
  // Enhanced filtering and sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('all'); // 'all', 'username', 'email', 'githubUsername', 'displayName'
  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all',
    accountType: 'all',
    mfa: 'all',
    mustChangePassword: 'all', // 'all' | 'required'
    github: 'all', // 'all' | 'missing'
    dateRange: {
      type: 'none',
      start: '',
      end: ''
    }
  });
  const [filterPresets, setFilterPresets] = useState(() => {
    const saved = localStorage.getItem('userManagementFilterPresets');
    return saved ? JSON.parse(saved) : [];
  });
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showQuickActions, setShowQuickActions] = useState(null);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [userActivityLogs, setUserActivityLogs] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [toast, setToast] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState({
    username: true,
    email: true,
    displayName: true,
    githubUsername: true,
    role: true,
    status: true,
    accountType: true,
    mfa: true,
    createdAt: true,
    lastLogin: true
  });
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  
  // Bulk operations
  const [showBulkOperations, setShowBulkOperations] = useState(false);
  const [bulkOperation, setBulkOperation] = useState({ type: '', value: '', subject: '', message: '' });
  const [editNotes, setEditNotes] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editPasswordExpiry, setEditPasswordExpiry] = useState('');
  const [savingUserMeta, setSavingUserMeta] = useState(false);
  
  // CSV Import
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);

  // Calculate statistics
  const statistics = useMemo(() => {
    const stats = {
      total: allUsers.length,
      active: allUsers.filter(u => u.isActive).length,
      inactive: allUsers.filter(u => !u.isActive).length,
      admins: allUsers.filter(u => u.role === 'admin').length,
      regularUsers: allUsers.filter(u => u.role === 'user').length,
      mfaEnabled: allUsers.filter(u => u.mfaEnabled).length,
      githubAccounts: allUsers.filter(u => u.accountType === 'github' || u.accountType === 'both').length,
      emailAccounts: allUsers.filter(u => u.accountType === 'email' || u.accountType === 'both').length,
      mustChangePassword: allUsers.filter(u => u.mustChangePassword).length,
      managers: allUsers.filter(u => u.role === 'manager').length,
      missingGithub: allUsers.filter(u => u.accountType !== 'github' && !u.githubUsername).length
    };
    return stats;
  }, [allUsers]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_URL}/admin/users`, {
        withCredentials: true
      });
      setAllUsers(response.data);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 403) {
        setError('You do not have permission to access this page');
        navigate('/dashboard');
      } else {
        setError('Failed to fetch users');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/dashboard');
      return;
    }

    if (user && user.role === 'admin') {
      fetchUsers();
      fetchAllRepos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  const fetchAllRepos = async () => {
    try {
      const response = await axios.get(`${API_URL}/repos`, {
        withCredentials: true
      });
      setAllRepos(response.data);
    } catch (error) {
      console.error('Error fetching repositories:', error);
    }
  };

  const fetchUserRepos = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/admin/users/${userId}/repositories`, {
        withCredentials: true
      });
      setUserRepos(prev => ({ ...prev, [userId]: response.data }));
    } catch (error) {
      console.error('Error fetching user repositories:', error);
      setUserRepos(prev => ({ ...prev, [userId]: [] }));
    }
  };

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = [...allUsers];

    // Apply search with field-specific search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(u => {
        if (searchField === 'username') {
          return u.username.toLowerCase().includes(searchLower);
        } else if (searchField === 'email') {
          return u.email.toLowerCase().includes(searchLower);
        } else if (searchField === 'githubUsername') {
          return (u.githubUsername && u.githubUsername.toLowerCase().includes(searchLower)) ||
                 (u.accountType === 'github' && u.username.toLowerCase().includes(searchLower));
        } else if (searchField === 'displayName') {
          return u.displayName && u.displayName.toLowerCase().includes(searchLower);
        } else {
          // Search all fields
          return (
            u.username.toLowerCase().includes(searchLower) ||
            u.email.toLowerCase().includes(searchLower) ||
            (u.displayName && u.displayName.toLowerCase().includes(searchLower)) ||
            (u.githubUsername && u.githubUsername.toLowerCase().includes(searchLower)) ||
            (u.accountType === 'github' && u.username.toLowerCase().includes(searchLower))
          );
        }
      });
    }

    // Apply filters
    if (filters.role !== 'all') {
      filtered = filtered.filter(u => u.role === filters.role);
    }
    if (filters.status !== 'all') {
      filtered = filtered.filter(u => filters.status === 'active' ? u.isActive : !u.isActive);
    }
    if (filters.accountType !== 'all') {
      filtered = filtered.filter(u => u.accountType === filters.accountType);
    }
    if (filters.mfa !== 'all') {
      filtered = filtered.filter(u => filters.mfa === 'enabled' ? u.mfaEnabled : !u.mfaEnabled);
    }
    if (filters.mustChangePassword === 'required') {
      filtered = filtered.filter(u => u.mustChangePassword);
    }
    if (filters.github === 'missing') {
      filtered = filtered.filter(u => u.accountType !== 'github' && !u.githubUsername);
    }

    // Apply date range filter
    if (filters.dateRange.type !== 'none' && (filters.dateRange.start || filters.dateRange.end)) {
      filtered = filtered.filter(u => {
        const dateField = filters.dateRange.type === 'createdAt' ? u.createdAt : u.lastLogin;
        if (!dateField) return filters.dateRange.type === 'lastLogin'; // Exclude users without lastLogin if filtering by it
        
        const userDate = new Date(dateField);
        const startDate = filters.dateRange.start ? new Date(filters.dateRange.start) : null;
        const endDate = filters.dateRange.end ? new Date(filters.dateRange.end) : null;
        
        if (startDate && endDate) {
          return userDate >= startDate && userDate <= endDate;
        } else if (startDate) {
          return userDate >= startDate;
        } else if (endDate) {
          return userDate <= endDate;
        }
        return true;
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle dates
        if (sortConfig.key === 'createdAt' || sortConfig.key === 'lastLogin') {
          aVal = aVal ? new Date(aVal) : new Date(0);
          bVal = bVal ? new Date(bVal) : new Date(0);
        }

        // Handle strings
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [allUsers, searchTerm, searchField, filters, sortConfig]);

  useEffect(() => {
    setUsers(filteredAndSortedUsers);
    setCurrentPage(1); // Reset to first page when filters change
  }, [filteredAndSortedUsers]);

  // Close column menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColumnMenu && !event.target.closest('.column-visibility-toggle')) {
        setShowColumnMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnMenu]);

  // Pagination logic
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return users.slice(startIndex, endIndex);
  }, [users, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(users.length / itemsPerPage);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const res = await axios.post(`${API_URL}/admin/users`, {
        username: formData.username,
        email: formData.email,
        password: formData.generatePassword ? undefined : formData.password,
        generatePassword: formData.generatePassword,
        displayName: formData.displayName,
        role: formData.role,
        githubUsername: formData.githubUsername || undefined,
        mustChangePassword: formData.mustChangePassword,
        inviteToGitHubOrg: formData.inviteToGitHubOrg
      }, {
        withCredentials: true
      });

      showToastMessage(res.data.message || 'User created successfully');
      setShowCreateForm(false);
      setFormData({
        username: '',
        email: '',
        password: '',
        displayName: '',
        role: 'user',
        githubUsername: '',
        mustChangePassword: true,
        inviteToGitHubOrg: true,
        generatePassword: true
      });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleInviteGitHubOrg = async (userId) => {
    try {
      setError('');
      const res = await inviteUserToGitHubOrg(userId);
      showToastMessage(res.data.message || 'GitHub org invite sent');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send GitHub org invite');
      setTimeout(() => setError(''), 8000);
    }
  };

  const handleDeleteUser = async (userId) => {
    const userToDelete = users.find(u => u._id === userId);
    const confirmMessage = userToDelete 
      ? `Are you sure you want to delete user "${userToDelete.username}"? This action cannot be undone.`
      : 'Are you sure you want to delete this user? This action cannot be undone.';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setError('');
      await axios.delete(`${API_URL}/admin/users/${userId}`, {
        withCredentials: true
      });
      showToastMessage('User deleted successfully');
      setSelectedUsers(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeactivateUser = async (userId) => {
    try {
      setError('');
      await axios.post(`${API_URL}/admin/users/${userId}/deactivate`, {}, {
        withCredentials: true
      });
      setSuccess('User deactivated successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate user');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await axios.post(`${API_URL}/admin/users/${userId}/activate`, {}, {
        withCredentials: true
      });
      setSuccess('User activated successfully');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate user');
    }
  };

  const handleBulkAction = async (action, value = null) => {
    if (selectedUsers.size === 0) {
      setError('Please select at least one user');
      return;
    }

    if (action === 'grant-repo-access') {
      const { repoId, expiresAt } = bulkOperation;
      const permission = bulkOperation.permission || 'read';
      if (!repoId || !permission) {
        setError('Repository and permission are required');
        return;
      }
      if (!window.confirm(`Grant ${permission} access to ${selectedUsers.size} user(s)?`)) return;
      try {
        const res = await bulkGrantRepoAccess(
          Array.from(selectedUsers),
          repoId,
          permission,
          expiresAt || undefined
        );
        showToastMessage(res.data.message || 'Access granted');
        setSelectedUsers(new Set());
        setShowBulkOperations(false);
        setBulkOperation({ type: '', value: '', subject: '', message: '' });
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to grant access');
      }
      return;
    }

    if (action === 'bulk-email') {
      const { subject, message } = bulkOperation;
      if (!subject || !message) {
        setError('Subject and message are required for bulk email');
        return;
      }
      if (!window.confirm(`Send email to ${selectedUsers.size} user(s)?`)) return;
      try {
        await axios.post(`${API_URL}/admin/bulk-email`, {
          userIds: Array.from(selectedUsers),
          subject,
          message
        }, { withCredentials: true });
        showToastMessage(`Email sent to ${selectedUsers.size} user(s)`);
        setSelectedUsers(new Set());
        setShowBulkOperations(false);
        setBulkOperation({ type: '', value: '', subject: '', message: '' });
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to send bulk email');
      }
      return;
    }

    let confirmMessage = `Are you sure you want to ${action} ${selectedUsers.size} user(s)?`;
    if (value) {
      confirmMessage = `Are you sure you want to ${action} ${selectedUsers.size} user(s) to "${value}"?`;
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      const promises = Array.from(selectedUsers).map(async (userId) => {
        try {
          if (action === 'activate') {
            return await axios.post(`${API_URL}/admin/users/${userId}/activate`, {}, { withCredentials: true });
          } else if (action === 'deactivate') {
            return await axios.post(`${API_URL}/admin/users/${userId}/deactivate`, {}, { withCredentials: true });
          } else if (action === 'change-role') {
            return await axios.post(`${API_URL}/admin/users/${userId}/change-role`, { role: value }, { withCredentials: true });
          } else if (action === 'set-github-username') {
            return await axios.post(`${API_URL}/admin/users/${userId}/set-github-username`, { githubUsername: value }, { withCredentials: true });
          } else if (action === 'reset-password') {
            return await axios.post(`${API_URL}/admin/users/${userId}/reset-password`, { newPassword: value }, { withCredentials: true });
          } else if (action === 'disable-mfa') {
            return await axios.post(`${API_URL}/admin/users/${userId}/disable-mfa`, {}, { withCredentials: true });
          } else if (action === 'delete') {
            return await axios.delete(`${API_URL}/admin/users/${userId}`, { withCredentials: true });
          }
        } catch (err) {
          console.error(`Error ${action} user ${userId}:`, err);
          throw err;
        }
      });

      await Promise.all(promises);
      showToastMessage(`${selectedUsers.size} user(s) ${action === 'change-role' ? 'role changed' : action === 'set-github-username' ? 'GitHub username set' : action === 'reset-password' ? 'password reset' : action === 'disable-mfa' ? 'MFA disabled' : action === 'delete' ? 'deleted' : action + 'd'} successfully`);
      setSelectedUsers(new Set());
      setShowBulkOperations(false);
      setBulkOperation({ type: '', value: '' });
      fetchUsers();
    } catch (err) {
      setError(`Failed to ${action} users: ${err.response?.data?.error || err.message}`);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleSaveUserMeta = async (userId) => {
    try {
      setSavingUserMeta(true);
      await axios.put(`${API_URL}/admin/users/${userId}/notes`, { notes: editNotes }, { withCredentials: true });
      await axios.put(`${API_URL}/admin/users/${userId}/tags`, {
        tags: editTags.split(',').map((t) => t.trim()).filter(Boolean)
      }, { withCredentials: true });
      await axios.put(`${API_URL}/admin/users/${userId}/password-expiry`, {
        expiryDate: editPasswordExpiry || null
      }, { withCredentials: true });
      showToastMessage('User details updated');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save user details');
    } finally {
      setSavingUserMeta(false);
    }
  };

  const handleResetPassword = async (userId, e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await axios.post(`${API_URL}/admin/users/${userId}/reset-password`, resetPasswordData, {
        withCredentials: true
      });
      showToastMessage('Password reset successfully');
      setShowResetPasswordForm(null);
      setResetPasswordData({ newPassword: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleDisableMFA = async (userId) => {
    if (!window.confirm('Are you sure you want to disable MFA for this user?')) {
      return;
    }

    try {
      await axios.post(`${API_URL}/admin/users/${userId}/disable-mfa`, {}, {
        withCredentials: true
      });
      setSuccess('MFA disabled successfully');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disable MFA');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.post(`${API_URL}/admin/users/${userId}/change-role`, { role: newRole }, {
        withCredentials: true
      });
      setSuccess('User role updated successfully');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change role');
    }
  };

  const handleSetGitHubUsername = async (userId, e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/admin/users/${userId}/set-github-username`, githubUsernameData, {
        withCredentials: true
      });
      setSuccess('GitHub username set successfully');
      setShowSetGitHubUsername(null);
      setGithubUsernameData({ githubUsername: '' });
      setGithubSuggestions([]);
      setShowSuggestions(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set GitHub username');
    }
  };

  const handleGrantRepoAccess = async (userId, repoId, permission, expiresAt) => {
    try {
      await axios.post(`${API_URL}/admin/users/${userId}/grant-repo-access`, {
        repoId,
        permission,
        expiresAt: expiresAt || undefined
      }, {
        withCredentials: true
      });
      setSuccess(expiresAt
        ? `Repository access granted until ${expiresAt}`
        : 'Repository access granted successfully');
      fetchUserRepos(userId);
      fetchAllRepos();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to grant repository access');
    }
  };

  const handleRevokeRepoAccess = async (userId, repoId) => {
    if (!window.confirm('Are you sure you want to revoke this repository access?')) {
      return;
    }

    try {
      await axios.post(`${API_URL}/admin/users/${userId}/revoke-repo-access`, {
        repoId
      }, {
        withCredentials: true
      });
      setSuccess('Repository access revoked successfully');
      fetchUserRepos(userId);
      fetchAllRepos();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke repository access');
    }
  };

  const getUsersToExport = () => {
    if (selectedUsers.size > 0) {
      return allUsers.filter(u => selectedUsers.has(u._id));
    }
    return users.length !== allUsers.length ? users : allUsers;
  };

  const handleExportCSV = () => {
    const usersToExport = getUsersToExport();
    const headers = ['Username', 'Email', 'Display Name', 'GitHub Username', 'Role', 'Status', 'Account Type', 'MFA Enabled', 'Created At', 'Last Login'];
    const rows = usersToExport.map(u => [
      u.username,
      u.email,
      u.displayName || '',
      u.githubUsername || (u.accountType === 'github' ? u.username : ''),
      u.role,
      u.isActive ? 'Active' : 'Inactive',
      u.accountType || 'email',
      u.mfaEnabled ? 'Yes' : 'No',
      u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '',
      u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const suffix = selectedUsers.size > 0 ? `_selected_${selectedUsers.size}` : '';
    a.download = `users_export${suffix}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportJSON = () => {
    const usersToExport = getUsersToExport();
    const exportData = usersToExport.map(u => ({
      username: u.username,
      email: u.email,
      displayName: u.displayName || '',
      githubUsername: u.githubUsername || (u.accountType === 'github' ? u.username : ''),
      role: u.role,
      status: u.isActive ? 'Active' : 'Inactive',
      accountType: u.accountType || 'email',
      mfaEnabled: u.mfaEnabled,
      createdAt: u.createdAt ? new Date(u.createdAt).toISOString() : null,
      lastLogin: u.lastLogin ? new Date(u.lastLogin).toISOString() : null,
      mustChangePassword: u.mustChangePassword || false
    }));

    const jsonContent = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const suffix = selectedUsers.size > 0 ? `_selected_${selectedUsers.size}` : '';
    a.download = `users_export${suffix}_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportExcel = () => {
    // For Excel export, we'll create a CSV with Excel-compatible format
    // In a production app, you might want to use a library like xlsx
    const usersToExport = getUsersToExport();
    const headers = ['Username', 'Email', 'Display Name', 'GitHub Username', 'Role', 'Status', 'Account Type', 'MFA Enabled', 'Created At', 'Last Login'];
    const rows = usersToExport.map(u => [
      u.username,
      u.email,
      u.displayName || '',
      u.githubUsername || (u.accountType === 'github' ? u.username : ''),
      u.role,
      u.isActive ? 'Active' : 'Inactive',
      u.accountType || 'email',
      u.mfaEnabled ? 'Yes' : 'No',
      u.createdAt ? new Date(u.createdAt).toLocaleString() : '',
      u.lastLogin ? new Date(u.lastLogin).toLocaleString() : ''
    ]);

    // Excel-compatible CSV with BOM for UTF-8
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join('\t'),
      ...rows.map(row => row.map(cell => String(cell).replace(/\t/g, ' ')).join('\t'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/tab-separated-values;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const suffix = selectedUsers.size > 0 ? `_selected_${selectedUsers.size}` : '';
    a.download = `users_export${suffix}_${new Date().toISOString().split('T')[0]}.xls`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSavePreset = () => {
    if (!presetName.trim()) {
      setError('Please enter a preset name');
      return;
    }

    const newPreset = {
      id: Date.now().toString(),
      name: presetName.trim(),
      filters: { ...filters },
      searchTerm,
      searchField
    };

    const updatedPresets = [...filterPresets, newPreset];
    setFilterPresets(updatedPresets);
    localStorage.setItem('userManagementFilterPresets', JSON.stringify(updatedPresets));
    setPresetName('');
    setShowPresetModal(false);
    setSuccess('Filter preset saved successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleLoadPreset = (preset) => {
    setFilters(preset.filters);
    setSearchTerm(preset.searchTerm || '');
    setSearchField(preset.searchField || 'all');
    setSuccess(`Loaded preset: ${preset.name}`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDeletePreset = (presetId) => {
    const updatedPresets = filterPresets.filter(p => p.id !== presetId);
    setFilterPresets(updatedPresets);
    localStorage.setItem('userManagementFilterPresets', JSON.stringify(updatedPresets));
    setSuccess('Preset deleted successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleImportCSV = async (e) => {
    e.preventDefault();
    if (!importFile) {
      setError('Please select a CSV file');
      return;
    }

    try {
      setImporting(true);
      setError('');
      
      // Parse CSV file
      const text = await importFile.text();
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        setError('CSV file must have at least a header row and one data row');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const dataRows = lines.slice(1).map(line => {
        const values = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());
        
        const row = {};
        headers.forEach((header, index) => {
          row[header.toLowerCase().replace(/\s+/g, '')] = values[index]?.replace(/^"|"$/g, '') || '';
        });
        return row;
      });

      // Validate and create users
      const results = [];
      for (const row of dataRows) {
        try {
          const userData = {
            username: row.username || row['username'],
            email: row.email || row['email'],
            password: row.password || row['password'] || generateRandomPassword(),
            displayName: row.displayname || row['display name'] || row['displayname'] || '',
            role: (row.role || row['role'] || 'user').toLowerCase(),
            githubUsername: row.githubusername || row['github username'] || row['githubusername'] || ''
          };

          if (!userData.username || !userData.email) {
            results.push({ success: false, user: userData, error: 'Missing username or email' });
            continue;
          }

          const response = await axios.post(`${API_URL}/admin/users`, userData, {
            withCredentials: true
          });
          results.push({ success: true, user: userData, data: response.data });
        } catch (err) {
          results.push({ 
            success: false, 
            user: row, 
            error: err.response?.data?.error || err.message 
          });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      setSuccess(`Import completed: ${successCount} users created, ${failCount} failed`);
      if (failCount > 0) {
        setError(`Failed to import ${failCount} user(s). Check console for details.`);
        console.error('Import errors:', results.filter(r => !r.success));
      }
      
      setShowImportModal(false);
      setImportFile(null);
      setImportPreview([]);
      fetchUsers();
    } catch (err) {
      setError(`Failed to import users: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setImportFile(file);
    
    // Preview first few rows
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim()).slice(0, 6);
      setImportPreview(lines);
    } catch (err) {
      console.error('Error reading file:', err);
    }
  };

  const handleSyncGitHubCollaborators = async () => {
    if (!window.confirm('This will sync all GitHub collaborators from your repositories into the portal. This may take a few moments. Continue?')) {
      return;
    }

    try {
      setSyncing(true);
      setError('');
      setSuccess('');
      setSyncStats(null);
      
      const response = await syncGitHubCollaborators();
      setSyncStats(response.data.stats);
      setSuccess(
        `Sync completed! Created ${response.data.stats.usersCreated} users` +
        (response.data.stats.portalInvitesSent
          ? ` (${response.data.stats.portalInvitesSent} portal invite emails sent)`
          : '') +
        (response.data.stats.orgInvitesSent
          ? `, ${response.data.stats.orgInvitesSent} GitHub org invite(s)`
          : '') +
        (response.data.stats.connectGitHubNeeded
          ? `, ${response.data.stats.connectGitHubNeeded} must sign in with GitHub (private email)`
          : '') +
        `, updated ${response.data.stats.usersUpdated} existing users.`
      );
      setTimeout(() => {
        setSuccess('');
        setSyncStats(null);
      }, 10000);
      
      // Refresh user list
      fetchUsers();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to sync GitHub collaborators';
      
      // If GitHub connection is required, show helpful message
      if (err.response?.data?.requiresGitHubConnection) {
        setError(
          <div>
            <strong>GitHub Connection Required</strong>
            <p style={{ marginTop: '8px', marginBottom: '8px' }}>{errorMessage}</p>
            <p style={{ marginTop: '8px', marginBottom: '4px', fontWeight: '500' }}>
              To connect your GitHub account:
            </p>
            <ol style={{ marginTop: '4px', marginBottom: '8px', paddingLeft: '20px' }}>
              <li>Log out of the portal</li>
              <li>Click "Sign in with GitHub" on the login page</li>
              <li>Authorize the application</li>
              <li>Log back in and try syncing again</li>
            </ol>
            <p style={{ marginTop: '8px', fontSize: '12px', color: '#656d76', fontStyle: 'italic' }}>
              Note: Alternatively, ensure at least one repository owner has connected their GitHub account.
            </p>
          </div>
        );
      } else {
        setError(errorMessage);
      }
      
      setTimeout(() => setError(''), 15000);
    } finally {
      setSyncing(false);
    }
  };

  const showToastMessage = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const emptyUserFilters = () => ({
    role: 'all',
    status: 'all',
    accountType: 'all',
    mfa: 'all',
    mustChangePassword: 'all',
    github: 'all',
    dateRange: { type: 'none', start: '', end: '' }
  });

  const applyQuickFilter = (preset) => {
    const next = emptyUserFilters();
    if (preset === 'all') {
      setSearchTerm('');
    } else if (preset === 'active') next.status = 'active';
    else if (preset === 'inactive') next.status = 'inactive';
    else if (preset === 'admins') next.role = 'admin';
    else if (preset === 'managers') next.role = 'manager';
    else if (preset === 'mfa') next.mfa = 'enabled';
    else if (preset === 'password') next.mustChangePassword = 'required';
    else if (preset === 'github') next.github = 'missing';
    setFilters(next);
    setCurrentPage(1);
  };

  const isQuickFilterActive = (preset) => {
    if (preset === 'all') {
      return filters.role === 'all' && filters.status === 'all' && filters.mfa === 'all'
        && filters.mustChangePassword === 'all' && filters.github === 'all';
    }
    if (preset === 'active') return filters.status === 'active' && filters.role === 'all';
    if (preset === 'inactive') return filters.status === 'inactive';
    if (preset === 'admins') return filters.role === 'admin';
    if (preset === 'managers') return filters.role === 'manager';
    if (preset === 'mfa') return filters.mfa === 'enabled';
    if (preset === 'password') return filters.mustChangePassword === 'required';
    if (preset === 'github') return filters.github === 'missing';
    return false;
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return d.toLocaleDateString();
  };

  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(u => u._id)));
    }
  };

  const handleViewUserDetails = async (userId) => {
    try {
      setLoadingUserDetails(true);
      setUserActivityLogs([]);
      const user = allUsers.find(u => u._id === userId);
      if (!user) return;

      if (!userRepos[userId]) {
        await fetchUserRepos(userId);
      }

      setSelectedUserDetails(user);
      setEditNotes(user.notes || '');
      setEditTags((user.tags || []).join(', '));
      setEditPasswordExpiry(user.passwordExpiry ? user.passwordExpiry.slice(0, 10) : '');

      setLoadingActivity(true);
      try {
        const activityRes = await axios.get(`${API_URL}/admin/users/${userId}/activity`, { withCredentials: true });
        setUserActivityLogs(Array.isArray(activityRes.data) ? activityRes.data : []);
      } catch {
        setUserActivityLogs([]);
      } finally {
        setLoadingActivity(false);
      }
    } catch (error) {
      console.error('Error loading user details:', error);
      setError('Failed to load user details');
    } finally {
      setLoadingUserDetails(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="user-management">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="page-header">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-lead">Create accounts, set who can approve, and find people who still need a GitHub username.</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={fetchUsers}
            title="Refresh"
          >
            Refresh
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setShowImportModal(true)}
            title="Import Users from CSV"
          >
            Import CSV
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleSyncGitHubCollaborators}
            title="Sync GitHub Collaborators"
            disabled={syncing}
          >
            {syncing ? 'Syncing…' : 'Sync from GitHub'}
          </button>
          <div className="export-dropdown-wrapper">
            <button
              className="btn btn-secondary"
              onClick={(e) => {
                e.stopPropagation();
                const menu = document.querySelector('.export-menu');
                if (menu) {
                  menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                }
              }}
              title="Export Users"
            >
              Export {selectedUsers.size > 0 && `(${selectedUsers.size})`}
            </button>
            <div className="export-menu">
              <button onClick={() => { handleExportCSV(); document.querySelector('.export-menu').style.display = 'none'; }}>
                📄 Export as CSV
              </button>
              <button onClick={() => { handleExportJSON(); document.querySelector('.export-menu').style.display = 'none'; }}>
                📋 Export as JSON
              </button>
              <button onClick={() => { handleExportExcel(); document.querySelector('.export-menu').style.display = 'none'; }}>
                📊 Export as Excel
              </button>
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateForm(true)}
          >
            + Create User
          </button>
        </div>
      </div>

      <div className="stats-dashboard">
        {[
          { key: 'all', value: statistics.total, label: 'All users' },
          { key: 'active', value: statistics.active, label: 'Active', className: 'stat-active' },
          { key: 'inactive', value: statistics.inactive, label: 'Inactive', className: 'stat-inactive' },
          { key: 'admins', value: statistics.admins, label: 'Admins', className: 'stat-admin' },
          { key: 'managers', value: statistics.managers, label: 'Managers' },
          { key: 'mfa', value: statistics.mfaEnabled, label: 'MFA on', className: 'stat-mfa' },
          { key: 'password', value: statistics.mustChangePassword, label: 'Password change', className: 'stat-password' },
          { key: 'github', value: statistics.missingGithub, label: 'No GitHub username' }
        ].map((card) => (
          <button
            key={card.key}
            type="button"
            className={`stat-card stat-button ${card.className || ''} ${isQuickFilterActive(card.key) ? 'is-selected' : ''}`}
            onClick={() => applyQuickFilter(card.key)}
          >
            <div className="stat-value">{card.value}</div>
            <div className="stat-label">{card.label}</div>
          </button>
        ))}
      </div>

      {/* Quick filter chips */}
      <div className="quick-filters">
        <span className="quick-filters-label">Quick filters:</span>
        {[
          { key: 'all', label: 'All' },
          { key: 'active', label: 'Active' },
          { key: 'inactive', label: 'Inactive' },
          { key: 'admins', label: 'Admins' },
          { key: 'mfa', label: 'MFA enabled' },
          { key: 'password', label: 'Password change required' },
          { key: 'managers', label: 'Managers' },
          { key: 'github', label: 'No GitHub username' }
        ].map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`quick-filter-chip ${isQuickFilterActive(key) ? 'active' : ''}`}
            onClick={() => applyQuickFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`} role="alert">
          {toast.message}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          {typeof error === 'string' ? error : error}
        </div>
      )}
      {success && <div className="alert alert-success">{success}</div>}
      
      {syncStats && (
        <div className="alert alert-info sync-stats">
          <h4>Sync Results:</h4>
          <ul>
            <li><strong>Repositories Processed:</strong> {syncStats.reposProcessed}</li>
            <li><strong>Unique Collaborators Found:</strong> {syncStats.uniqueCollaborators}</li>
            <li><strong>Total Collaborators Found:</strong> {syncStats.collaboratorsFound}</li>
            <li><strong>New Users Created:</strong> {syncStats.usersCreated}</li>
            <li><strong>Portal Invite Emails Sent:</strong> {syncStats.portalInvitesSent || 0}</li>
            <li><strong>GitHub Org Invites (private email):</strong> {syncStats.orgInvitesSent || 0}</li>
            <li><strong>Need GitHub sign-in:</strong> {syncStats.connectGitHubNeeded || 0}</li>
            <li><strong>Existing Users Updated:</strong> {syncStats.usersUpdated}</li>
            {syncStats.errors && syncStats.errors.length > 0 && (
              <li><strong>Errors:</strong> {syncStats.errors.length}
                <details style={{ marginTop: '8px' }}>
                  <summary>View Errors</summary>
                  <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                    {syncStats.errors.map((err, idx) => (
                      <li key={idx} style={{ fontSize: '12px', color: '#cf222e' }}>{err}</li>
                    ))}
                  </ul>
                </details>
              </li>
            )}
          </ul>
        </div>
      )}

      {showCreateForm && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Create New User</h2>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setShowCreateForm(false);
                setFormData({
                  username: '',
                  email: '',
                  password: '',
                  displayName: '',
                  role: 'user',
                  githubUsername: '',
                  mustChangePassword: true,
                  inviteToGitHubOrg: true,
                  generatePassword: true
                });
                setError('');
              }}
            >
              Cancel
            </button>
          </div>
          <p className="form-hint" style={{ marginBottom: '1rem' }}>
            Creates a <strong>portal login</strong>. GitHub personal accounts cannot be created via API —
            the welcome email guides the user to create a GitHub account, then they log in and request repo access.
            Optionally invite them to your GitHub org by email (<code>GITHUB_ORG</code>).
          </p>
          <form onSubmit={handleCreateUser} className="user-form">
            <div className="form-group">
              <label className="form-label">Username *</label>
              <input
                type="text"
                className="form-input"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input
                type="email"
                className="form-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
              <p className="form-hint">Used for portal login and GitHub org invite.</p>
            </div>
            <div className="form-group form-group-checkbox">
              <label className="form-label checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.generatePassword}
                  onChange={(e) => setFormData({ ...formData, generatePassword: e.target.checked })}
                />
                Auto-generate temporary password (sent by email)
              </label>
            </div>
            {!formData.generatePassword && (
              <div className="form-group">
                <label className="form-label">Password *</label>
                <input
                  type="password"
                  className="form-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!formData.generatePassword}
                  minLength={8}
                />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Display Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Role</label>
              <select
                className="form-select"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">GitHub username (if they already have one)</label>
              <input
                type="text"
                className="form-input"
                value={formData.githubUsername}
                onChange={(e) => setFormData({ ...formData, githubUsername: e.target.value })}
                placeholder="Leave blank if they will create a GitHub account"
              />
            </div>
            <div className="form-group form-group-checkbox">
              <label className="form-label checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.mustChangePassword}
                  onChange={(e) => setFormData({ ...formData, mustChangePassword: e.target.checked })}
                />
                Force password change on first login
              </label>
            </div>
            <div className="form-group form-group-checkbox">
              <label className="form-label checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.inviteToGitHubOrg}
                  onChange={(e) => setFormData({ ...formData, inviteToGitHubOrg: e.target.checked })}
                />
                Invite to GitHub organization by email (requires GITHUB_ORG + admin GitHub OAuth)
              </label>
            </div>
            <button type="submit" className="btn btn-primary">Create User &amp; Send Onboarding Email</button>
          </form>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            Directory 
            {users.length !== allUsers.length ? (
              <span className="user-count-filtered">
                ({users.length} of {allUsers.length})
              </span>
            ) : (
              <span>({allUsers.length})</span>
            )}
          </h2>
          <div className="card-actions">
            <div className="column-visibility-toggle">
              <div className="column-visibility-wrapper-header">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowColumnMenu(!showColumnMenu);
                  }}
                  title="Column Visibility"
                >
                  ⚙️ Columns
                </button>
                {showColumnMenu && (
                  <div className="column-visibility-dropdown show">
                    <div className="column-visibility-header">
                      <h4>Show/Hide Columns</h4>
                    </div>
                    <div className="column-visibility-options">
                      {Object.entries(visibleColumns).map(([key, visible]) => (
                        <label key={key} className="column-visibility-option">
                          <input
                            type="checkbox"
                            checked={visible}
                            onChange={(e) => setVisibleColumns({ ...visibleColumns, [key]: e.target.checked })}
                          />
                          <span>{key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="search-wrapper">
              <select
                className="form-select search-field-select"
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                title="Search field"
              >
                <option value="all">All Fields</option>
                <option value="username">Username</option>
                <option value="email">Email</option>
                <option value="githubUsername">GitHub Username</option>
                <option value="displayName">Display Name</option>
              </select>
              <input
                type="text"
                className="form-input search-input"
                placeholder={`Search ${searchField === 'all' ? 'users' : searchField}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label>Role:</label>
              <select
                className="form-select filter-select"
                value={filters.role}
                onChange={(e) => setFilters({ ...filters, role: e.target.value })}
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="user">User</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Status:</label>
              <select
                className="form-select filter-select"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Account Type:</label>
              <select
                className="form-select filter-select"
                value={filters.accountType}
                onChange={(e) => setFilters({ ...filters, accountType: e.target.value })}
              >
                <option value="all">All Types</option>
                <option value="github">GitHub</option>
                <option value="email">Email</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div className="filter-group">
              <label>MFA:</label>
              <select
                className="form-select filter-select"
                value={filters.mfa}
                onChange={(e) => setFilters({ ...filters, mfa: e.target.value })}
              >
                <option value="all">All</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Date Range:</label>
              <select
                className="form-select filter-select"
                value={filters.dateRange.type}
                onChange={(e) => setFilters({ 
                  ...filters, 
                  dateRange: { ...filters.dateRange, type: e.target.value, start: '', end: '' }
                })}
              >
                <option value="none">No Date Filter</option>
                <option value="createdAt">Created Date</option>
                <option value="lastLogin">Last Login</option>
              </select>
            </div>
            {filters.dateRange.type !== 'none' && (
              <>
                <div className="filter-group">
                  <label>From:</label>
                  <input
                    type="date"
                    className="form-input filter-date-input"
                    value={filters.dateRange.start}
                    onChange={(e) => setFilters({ 
                      ...filters, 
                      dateRange: { ...filters.dateRange, start: e.target.value }
                    })}
                  />
                </div>
                <div className="filter-group">
                  <label>To:</label>
                  <input
                    type="date"
                    className="form-input filter-date-input"
                    value={filters.dateRange.end}
                    onChange={(e) => setFilters({ 
                      ...filters, 
                      dateRange: { ...filters.dateRange, end: e.target.value }
                    })}
                  />
                </div>
              </>
            )}
          </div>
          <div className="filters-actions">
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setFilters({
                  role: 'all',
                  status: 'all',
                  accountType: 'all',
                  mfa: 'all',
                  mustChangePassword: 'all',
                  github: 'all',
                  dateRange: { type: 'none', start: '', end: '' }
                });
                setSearchTerm('');
                setSearchField('all');
              }}
            >
              Clear Filters
            </button>
            <div className="preset-wrapper">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowPresetModal(true)}
              >
                💾 Save Preset
              </button>
              {filterPresets.length > 0 && (
                <div className="preset-dropdown">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      const menu = document.querySelector('.preset-menu');
                      if (menu) {
                        menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                      }
                    }}
                  >
                    📂 Load Preset ▼
                  </button>
                  <div className="preset-menu">
                    {filterPresets.map(preset => (
                      <div key={preset.id} className="preset-item">
                        <button onClick={() => { handleLoadPreset(preset); document.querySelector('.preset-menu').style.display = 'none'; }}>
                          {preset.name}
                        </button>
                        <button
                          className="preset-delete"
                          onClick={() => handleDeletePreset(preset.id)}
                          title="Delete preset"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedUsers.size > 0 && (
          <div className="bulk-actions">
            <span>{selectedUsers.size} user(s) selected</span>
            <div className="bulk-buttons">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => handleBulkAction('activate')}
              >
                Activate
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handleBulkAction('deactivate')}
              >
                Deactivate
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowBulkOperations(true)}
              >
                More Actions ▼
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setSelectedUsers(new Set())}
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {/* Advanced Bulk Operations Modal */}
        {showBulkOperations && selectedUsers.size > 0 && (
          <div className="modal-overlay" onClick={() => setShowBulkOperations(false)}>
            <div className="modal-content bulk-operations-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Bulk Operations ({selectedUsers.size} users)</h3>
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowBulkOperations(false);
                    setBulkOperation({ type: '', value: '' });
                  }}
                >
                  ×
                </button>
              </div>
              <div className="bulk-operations-content">
                <div className="form-group">
                  <label className="form-label">Operation</label>
                  <select
                    className="form-select"
                    value={bulkOperation.type}
                    onChange={(e) => setBulkOperation({ ...bulkOperation, type: e.target.value, value: '' })}
                  >
                    <option value="">Select operation...</option>
                    <option value="change-role">Change Role</option>
                    <option value="set-github-username">Set GitHub Username</option>
                    <option value="reset-password">Reset Password</option>
                    <option value="disable-mfa">Disable MFA</option>
                    <option value="grant-repo-access">Grant repository access</option>
                    <option value="bulk-email">Send Bulk Email</option>
                    <option value="delete">Delete Users</option>
                  </select>
                </div>

                {bulkOperation.type === 'change-role' && (
                  <div className="form-group">
                    <label className="form-label">New Role</label>
                    <select
                      className="form-select"
                      value={bulkOperation.value}
                      onChange={(e) => setBulkOperation({ ...bulkOperation, value: e.target.value })}
                    >
                      <option value="">Select role...</option>
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="user">User</option>
                    </select>
                  </div>
                )}

                {bulkOperation.type === 'set-github-username' && (
                  <div className="form-group">
                    <label className="form-label">GitHub Username</label>
                    <input
                      type="text"
                      className="form-input"
                      value={bulkOperation.value}
                      onChange={(e) => setBulkOperation({ ...bulkOperation, value: e.target.value })}
                      placeholder="github-username"
                    />
                    <p className="form-hint">This will set the same GitHub username for all selected users</p>
                  </div>
                )}

                {bulkOperation.type === 'reset-password' && (
                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <input
                      type="password"
                      className="form-input"
                      value={bulkOperation.value}
                      onChange={(e) => setBulkOperation({ ...bulkOperation, value: e.target.value })}
                      placeholder="Enter new password (min. 8 characters)"
                      minLength={8}
                    />
                    <p className="form-hint">All selected users will be forced to change this password on next login</p>
                  </div>
                )}

                {bulkOperation.type === 'grant-repo-access' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Repository</label>
                      <select
                        className="form-select"
                        value={bulkOperation.repoId || ''}
                        onChange={(e) => setBulkOperation({ ...bulkOperation, repoId: e.target.value })}
                      >
                        <option value="">Select repository...</option>
                        {allRepos.map((repo) => (
                          <option key={repo._id} value={repo._id}>{repo.fullName}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Permission</label>
                      <select
                        className="form-select"
                        value={bulkOperation.permission || 'read'}
                        onChange={(e) => setBulkOperation({ ...bulkOperation, permission: e.target.value })}
                      >
                        <option value="read">Read</option>
                        <option value="write">Write</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Expiry date (optional)</label>
                      <input
                        type="date"
                        className="form-input"
                        value={bulkOperation.expiresAt || ''}
                        onChange={(e) => setBulkOperation({ ...bulkOperation, expiresAt: e.target.value })}
                      />
                      <p className="form-hint">Applied to every selected user. Policy default expiry is used when this is empty.</p>
                    </div>
                  </>
                )}

                {bulkOperation.type === 'bulk-email' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Subject</label>
                      <input
                        type="text"
                        className="form-input"
                        value={bulkOperation.subject}
                        onChange={(e) => setBulkOperation({ ...bulkOperation, subject: e.target.value })}
                        placeholder="Email subject"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Message</label>
                      <textarea
                        className="form-input"
                        rows={5}
                        value={bulkOperation.message}
                        onChange={(e) => setBulkOperation({ ...bulkOperation, message: e.target.value })}
                        placeholder="Use {{username}} and {{displayName}} for personalization"
                      />
                    </div>
                  </>
                )}

                {bulkOperation.type === 'delete' && (
                  <div className="alert alert-warning">
                    <strong>Warning:</strong> This will permanently delete {selectedUsers.size} user(s). This action cannot be undone.
                  </div>
                )}

                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      if (!bulkOperation.type) {
                        setError('Please select an operation');
                        return;
                      }
                      if (bulkOperation.type === 'grant-repo-access') {
                        if (!bulkOperation.repoId) {
                          setError('Select a repository');
                          return;
                        }
                        handleBulkAction('grant-repo-access');
                        return;
                      }
                      if (bulkOperation.type === 'bulk-email') {
                        if (!bulkOperation.subject || !bulkOperation.message) {
                          setError('Subject and message are required');
                          return;
                        }
                        handleBulkAction('bulk-email');
                        return;
                      }
                      const needsValue = ['change-role', 'set-github-username', 'reset-password'].includes(bulkOperation.type);
                      if (needsValue && !bulkOperation.value) {
                        setError('Please provide a value for this operation');
                        return;
                      }
                      if (bulkOperation.type === 'reset-password' && bulkOperation.value.length < 8) {
                        setError('Password must be at least 8 characters');
                        return;
                      }
                      handleBulkAction(bulkOperation.type, bulkOperation.value);
                    }}
                    disabled={
                      !bulkOperation.type ||
                      (['change-role', 'set-github-username', 'reset-password'].includes(bulkOperation.type) && !bulkOperation.value) ||
                      (bulkOperation.type === 'bulk-email' && (!bulkOperation.subject || !bulkOperation.message)) ||
                      (bulkOperation.type === 'grant-repo-access' && !bulkOperation.repoId)
                    }
                  >
                    Execute Operation
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowBulkOperations(false);
                      setBulkOperation({ type: '', value: '' });
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {allUsers.length === 0 ? (
          <div className="empty-state">
            <p>No users found.</p>
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <p>No users match your search/filters.</p>
          </div>
        ) : (
          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedUsers.size === paginatedUsers.length && paginatedUsers.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  {visibleColumns.username && (
                    <th onClick={() => handleSort('username')} className="sortable col-user">
                      Username {sortConfig.key === 'username' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.email && (
                    <th onClick={() => handleSort('email')} className="sortable col-email">
                      Email {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.displayName && <th className="col-name">Display Name</th>}
                  {visibleColumns.githubUsername && <th className="col-github">GitHub Username</th>}
                  {visibleColumns.role && (
                    <th onClick={() => handleSort('role')} className="sortable col-role">
                      Role {sortConfig.key === 'role' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.status && (
                    <th onClick={() => handleSort('isActive')} className="sortable col-status">
                      Status {sortConfig.key === 'isActive' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.accountType && <th className="col-account">Account Type</th>}
                  {visibleColumns.mfa && <th className="col-mfa">MFA</th>}
                  {visibleColumns.createdAt && (
                    <th onClick={() => handleSort('createdAt')} className="sortable col-date">
                      Created {sortConfig.key === 'createdAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.lastLogin && (
                    <th onClick={() => handleSort('lastLogin')} className="sortable col-date">
                      Last Login {sortConfig.key === 'lastLogin' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((u) => (
                  <React.Fragment key={u._id}>
                    <tr 
                      className="user-row-clickable"
                      onClick={() => handleViewUserDetails(u._id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(u._id)}
                          onChange={() => toggleSelectUser(u._id)}
                          disabled={u._id === user?.id}
                        />
                      </td>
                      {visibleColumns.username && (
                        <td className="col-user">
                          <div className="user-cell">
                            {u.avatarUrl && (
                              <img src={u.avatarUrl} alt={u.username} className="user-avatar-small" />
                            )}
                            <span>{withBreaks(u.username)}</span>
                            {u.mustChangePassword && (
                              <span className="password-change-badge" title="Password change required">🔒</span>
                            )}
                          </div>
                        </td>
                      )}
                      {visibleColumns.email && <td className="col-email">{withBreaks(u.email)}</td>}
                      {visibleColumns.displayName && <td className="col-name">{withBreaks(u.displayName || '-')}</td>}
                      {visibleColumns.githubUsername && (
                        <td className="col-github">
                          {u.githubUsername ? (
                            <span className="github-username">{withBreaks(u.githubUsername)}</span>
                          ) : u.accountType === 'github' ? (
                            <span className="github-username">{withBreaks(u.username)}</span>
                          ) : (
                            <span className="no-github">Needs a username</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.role && (
                        <td className="col-role" onClick={(e) => e.stopPropagation()}>
                          <select
                            value={u.role}
                            onChange={(e) => handleChangeRole(u._id, e.target.value)}
                            className="form-select role-select"
                            disabled={u._id === user?.id}
                          >
                            <option value="user">User</option>
                            <option value="manager">Manager</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                      )}
                      {visibleColumns.status && (
                        <td className="col-status">
                          <span className={`status-badge ${u.isActive ? 'active' : 'inactive'}`}>
                            {u.isActive ? '✓ Active' : '✗ Inactive'}
                          </span>
                        </td>
                      )}
                      {visibleColumns.accountType && (
                        <td className="col-account">
                          <span className="account-type-badge">{u.accountType || 'email'}</span>
                        </td>
                      )}
                      {visibleColumns.mfa && (
                        <td className="col-mfa">
                          {u.mfaEnabled ? (
                            <span className="mfa-badge enabled">✓ Enabled</span>
                          ) : (
                            <span className="mfa-badge disabled">✗ Disabled</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.createdAt && <td className="date-cell col-date">{formatDate(u.createdAt)}</td>}
                      {visibleColumns.lastLogin && <td className="date-cell col-date">{formatDate(u.lastLogin)}</td>}
                      <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                        <div className="action-buttons">
                          <div className="quick-actions-wrapper">
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowQuickActions(showQuickActions === u._id ? null : u._id);
                              }}
                            >
                              ⋮
                            </button>
                            {showQuickActions === u._id && (
                              <div className="quick-actions-menu">
                                {!u.isActive ? (
                                  <button onClick={() => { handleActivateUser(u._id); setShowQuickActions(null); }}>
                                    Activate
                                  </button>
                                ) : (
                                  <button onClick={() => { if (window.confirm(`Deactivate ${u.username}?`)) { handleDeactivateUser(u._id); setShowQuickActions(null); } }} disabled={u._id === user?.id}>
                                    Deactivate
                                  </button>
                                )}
                                <button onClick={() => { setShowResetPasswordForm(u._id); setShowQuickActions(null); }}>
                                  Reset Password
                                </button>
                                {u.mfaEnabled && (
                                  <button onClick={() => { if (window.confirm('Disable MFA?')) { handleDisableMFA(u._id); setShowQuickActions(null); } }}>
                                    Disable MFA
                                  </button>
                                )}
                                {!u.githubUsername && u.accountType !== 'github' && (
                                  <button onClick={() => { setShowSetGitHubUsername(u._id); setGithubUsernameData({ githubUsername: '' }); setShowQuickActions(null); }}>
                                    Set GitHub
                                  </button>
                                )}
                                <button onClick={() => { handleInviteGitHubOrg(u._id); setShowQuickActions(null); }}>
                                  Invite to GitHub Org
                                </button>
                                <button onClick={() => {
                                  if (showRepoAccess === u._id) {
                                    setShowRepoAccess(null);
                                  } else {
                                    setShowRepoAccess(u._id);
                                    if (!userRepos[u._id]) {
                                      fetchUserRepos(u._id);
                                    }
                                  }
                                  setShowQuickActions(null);
                                }}>
                                  {showRepoAccess === u._id ? 'Hide Repos' : 'View Repos'}
                                </button>
                                <button onClick={() => { if (window.confirm(`Delete ${u.username}?`)) { handleDeleteUser(u._id); setShowQuickActions(null); } }} disabled={u._id === user?.id} className="danger">
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                    {showRepoAccess === u._id && (
                      <tr className="repo-access-row">
                        <td colSpan={Object.values(visibleColumns).filter(v => v).length + 2}>
                          <div className="repo-access-panel">
                            <div className="repo-access-header">
                              <h3>Repository Access for {u.username}</h3>
                              {!u.githubUsername && u.accountType !== 'github' && (
                                <div className="alert alert-info">
                                  ⚠️ This user needs a GitHub username to be granted repository access. 
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => {
                                      setShowSetGitHubUsername(u._id);
                                      setGithubUsernameData({ githubUsername: '' });
                                      setGithubSuggestions([]);
                                      setShowSuggestions(false);
                                    }}
                                    style={{ marginLeft: '8px' }}
                                  >
                                    Set GitHub Username
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="repo-access-content">
                              <div className="user-repos-section">
                                <h4>Current Access ({userRepos[u._id]?.length || 0})</h4>
                                {userRepos[u._id] && userRepos[u._id].length > 0 ? (
                                  <PagedSlice items={userRepos[u._id]} pageSize={10} label="repositories">
                                  {(pageRepos) => (
                                  <div className="repos-list">
                                    {pageRepos.map(repo => (
                                      <div key={repo._id} className="repo-access-item">
                                        <div className="repo-info">
                                          <a 
                                            href={repo.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="repo-link"
                                          >
                                            {repo.fullName}
                                          </a>
                                          <span className={`permission-badge permission-${repo.userPermission}`}>
                                            {repo.userPermission === 'admin' ? 'Admin' : repo.userPermission === 'write' ? 'Write' : 'Read'}
                                          </span>
                                          {repo.accessExpiresAt && (
                                            <span className="repo-stat">Expires {formatAccessExpiry(repo.accessExpiresAt)}</span>
                                          )}
                                        </div>
                                        <button
                                          className="btn btn-danger btn-sm"
                                          onClick={() => handleRevokeRepoAccess(u._id, repo._id)}
                                        >
                                          Revoke
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                  )}
                                  </PagedSlice>
                                ) : (
                                  <p className="empty-message">No repository access</p>
                                )}
                              </div>
                              {u.githubUsername && (
                                <div className="grant-access-section">
                                  <h4>Grant Access to Repository</h4>
                                  <div className="grant-access-form">
                                    <div className="form-group">
                                      <label className="form-label">Repository</label>
                                      <select
                                        className="form-select"
                                        id={`repo-select-${u._id}`}
                                        defaultValue=""
                                      >
                                        <option value="">Select repository...</option>
                                        {allRepos
                                          .filter(repo => {
                                            const userReposList = userRepos[u._id] || [];
                                            return !userReposList.some(ur => ur._id === repo._id);
                                          })
                                          .map(repo => (
                                            <option key={repo._id} value={repo._id}>
                                              {repo.fullName}
                                            </option>
                                          ))}
                                      </select>
                                    </div>
                                    <div className="form-group">
                                      <label className="form-label">Permission</label>
                                      <select
                                        className="form-select"
                                        id={`permission-select-${u._id}`}
                                        defaultValue="read"
                                      >
                                        <option value="read">Read</option>
                                        <option value="write">Write</option>
                                        <option value="admin">Admin</option>
                                      </select>
                                    </div>
                                    <div className="form-group">
                                      <label className="form-label">Expires (optional)</label>
                                      <input
                                        type="date"
                                        className="form-input"
                                        id={`expiry-select-${u._id}`}
                                        min={new Date().toISOString().slice(0, 10)}
                                      />
                                      <p className="form-hint">Leave empty for permanent. On expiry, access is revoked and they must request again.</p>
                                    </div>
                                    <button
                                      className="btn btn-primary btn-sm"
                                      onClick={() => {
                                        const repoSelect = document.getElementById(`repo-select-${u._id}`);
                                        const permissionSelect = document.getElementById(`permission-select-${u._id}`);
                                        const expirySelect = document.getElementById(`expiry-select-${u._id}`);
                                        const repoId = repoSelect.value;
                                        const permission = permissionSelect.value;
                                        const expiresAt = expirySelect?.value || undefined;
                                        
                                        if (repoId) {
                                          handleGrantRepoAccess(u._id, repoId, permission, expiresAt);
                                          repoSelect.value = '';
                                          if (expirySelect) expirySelect.value = '';
                                        } else {
                                          setError('Please select a repository');
                                        }
                                      }}
                                    >
                                      Grant Access
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showResetPasswordForm && (
        <div className="modal-overlay" onClick={() => setShowResetPasswordForm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reset Password</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowResetPasswordForm(null);
                  setResetPasswordData({ newPassword: '' });
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={(e) => handleResetPassword(showResetPasswordForm, e)}>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={resetPasswordData.newPassword}
                  onChange={(e) => setResetPasswordData({ newPassword: e.target.value })}
                  required
                  minLength={8}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Reset Password</button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowResetPasswordForm(null);
                    setResetPasswordData({ newPassword: '' });
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSetGitHubUsername && (
        <div className="modal-overlay" onClick={() => setShowSetGitHubUsername(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Set GitHub Username</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowSetGitHubUsername(null);
                  setGithubUsernameData({ githubUsername: '' });
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={(e) => handleSetGitHubUsername(showSetGitHubUsername, e)}>
              <div className="form-group">
                <label className="form-label">GitHub Username</label>
                <input
                  type="text"
                  className="form-input"
                  value={githubUsernameData.githubUsername}
                  onChange={(e) => {
                    const value = e.target.value;
                    setGithubUsernameData({ githubUsername: value });
                    
                    if (value.length > 0) {
                      const currentUser = allUsers.find(u => u._id === showSetGitHubUsername);
                      const suggestions = [];
                      
                      if (currentUser && currentUser.username && 
                          currentUser.username.toLowerCase().includes(value.toLowerCase()) &&
                          currentUser.username.toLowerCase() !== value.toLowerCase()) {
                        suggestions.push(currentUser.username);
                      }
                      
                      const githubSuggestions = allUsers
                        .filter(u => {
                          const githubUsername = u.githubUsername || (u.accountType === 'github' ? u.username : null);
                          return githubUsername && 
                                 githubUsername.toLowerCase().includes(value.toLowerCase()) &&
                                 githubUsername.toLowerCase() !== value.toLowerCase() &&
                                 !suggestions.includes(githubUsername);
                        })
                        .map(u => u.githubUsername || u.username)
                        .filter((v, i, arr) => arr.indexOf(v) === i);
                      
                      suggestions.push(...githubSuggestions);
                      const finalSuggestions = suggestions.slice(0, 5);
                      
                      setGithubSuggestions(finalSuggestions);
                      setShowSuggestions(finalSuggestions.length > 0);
                    } else {
                      const topSuggestions = allUsers
                        .map(u => u.githubUsername || (u.accountType === 'github' ? u.username : null))
                        .filter(Boolean)
                        .filter((v, i, arr) => arr.indexOf(v) === i)
                        .slice(0, 5);
                      
                      setGithubSuggestions(topSuggestions);
                      setShowSuggestions(false);
                    }
                  }}
                  onFocus={(e) => {
                    if (githubUsernameData.githubUsername.length === 0) {
                      const topSuggestions = allUsers
                        .map(u => u.githubUsername || (u.accountType === 'github' ? u.username : null))
                        .filter(Boolean)
                        .filter((v, i, arr) => arr.indexOf(v) === i)
                        .slice(0, 5);
                      setGithubSuggestions(topSuggestions);
                      setShowSuggestions(topSuggestions.length > 0);
                    } else if (githubSuggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowSuggestions(false), 200);
                  }}
                  required
                  placeholder="github-username"
                  autoComplete="off"
                />
                {showSuggestions && githubSuggestions.length > 0 && (
                  <div className="autocomplete-suggestions">
                    {githubSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="autocomplete-suggestion-item"
                        onClick={() => {
                          setGithubUsernameData({ githubUsername: suggestion });
                          setShowSuggestions(false);
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
                <p className="form-hint">This username will be used to grant repository access on GitHub</p>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Set GitHub Username</button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowSetGitHubUsername(null);
                    setGithubUsernameData({ githubUsername: '' });
                    setGithubSuggestions([]);
                    setShowSuggestions(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {selectedUserDetails && (
        <div className="modal-overlay" onClick={() => setSelectedUserDetails(null)}>
          <div className="modal-content user-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>User Details</h3>
              <button
                className="modal-close"
                onClick={() => setSelectedUserDetails(null)}
              >
                ×
              </button>
            </div>

            {loadingUserDetails ? (
              <div className="spinner"></div>
            ) : (
              <div className="user-details-content">
                {/* User Profile Section */}
                <div className="user-details-section">
                  <h4>Profile Information</h4>
                  <div className="user-profile-header">
                    {selectedUserDetails.avatarUrl && (
                      <img 
                        src={selectedUserDetails.avatarUrl} 
                        alt={selectedUserDetails.username}
                        className="user-avatar-large"
                      />
                    )}
                    <div className="user-profile-info">
                      <h2>{selectedUserDetails.displayName || selectedUserDetails.username}</h2>
                      <p className="user-username">@{selectedUserDetails.username}</p>
                      {selectedUserDetails.mustChangePassword && (
                        <span className="password-change-badge" title="Password change required">🔒 Password Change Required</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Account Details Section */}
                <div className="user-details-section">
                  <h4>Account Details</h4>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{selectedUserDetails.email}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Display Name:</span>
                      <span className="detail-value">{selectedUserDetails.displayName || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">GitHub Username:</span>
                      <span className="detail-value">
                        {selectedUserDetails.githubUsername ? (
                          <a 
                            href={`https://github.com/${selectedUserDetails.githubUsername}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="github-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {selectedUserDetails.githubUsername}
                          </a>
                        ) : selectedUserDetails.accountType === 'github' ? (
                          <a 
                            href={`https://github.com/${selectedUserDetails.username}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="github-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {selectedUserDetails.username}
                          </a>
                        ) : (
                          <span className="no-github">Not set</span>
                        )}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Role:</span>
                      <span className="detail-value">
                        <span className={`role-badge ${selectedUserDetails.role}`}>
                          {selectedUserDetails.role === 'admin' ? '👑 Admin' : 'User'}
                        </span>
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <span className="detail-value">
                        <span className={`status-badge ${selectedUserDetails.isActive ? 'active' : 'inactive'}`}>
                          {selectedUserDetails.isActive ? '✓ Active' : '✗ Inactive'}
                        </span>
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Account Type:</span>
                      <span className="detail-value">
                        <span className="account-type-badge">{selectedUserDetails.accountType || 'email'}</span>
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">MFA:</span>
                      <span className="detail-value">
                        {selectedUserDetails.mfaEnabled ? (
                          <span className="mfa-badge enabled">✓ Enabled</span>
                        ) : (
                          <span className="mfa-badge disabled">✗ Disabled</span>
                        )}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Account Created:</span>
                      <span className="detail-value">
                        {selectedUserDetails.createdAt 
                          ? new Date(selectedUserDetails.createdAt).toLocaleString()
                          : 'Unknown'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Last Login:</span>
                      <span className="detail-value">
                        {formatDate(selectedUserDetails.lastLogin)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Repository Access Section */}
                <div className="user-details-section">
                  <h4>Repository Access ({userRepos[selectedUserDetails._id]?.length || 0})</h4>
                  {userRepos[selectedUserDetails._id] && userRepos[selectedUserDetails._id].length > 0 ? (
                    <PagedSlice items={userRepos[selectedUserDetails._id]} pageSize={10} label="repositories">
                    {(pageRepos) => (
                    <div className="user-repos-list">
                      {pageRepos.map(repo => (
                        <div key={repo._id} className="user-repo-item">
                          <div className="user-repo-info">
                            <a 
                              href={repo.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="repo-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {repo.fullName}
                            </a>
                            <span className={`permission-badge permission-${repo.userPermission}`}>
                              {repo.userPermission === 'admin' ? 'Admin' : repo.userPermission === 'write' ? 'Write' : 'Read'}
                            </span>
                            {repo.accessExpiresAt && (
                              <span className="repo-stat">Expires {formatAccessExpiry(repo.accessExpiresAt)}</span>
                            )}
                          </div>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Revoke access to ${repo.fullName}?`)) {
                                handleRevokeRepoAccess(selectedUserDetails._id, repo._id);
                              }
                            }}
                          >
                            Revoke
                          </button>
                        </div>
                      ))}
                    </div>
                    )}
                    </PagedSlice>
                  ) : (
                    <div className="empty-repos-message">
                      <p>No repository access</p>
                      {!selectedUserDetails.githubUsername && selectedUserDetails.accountType !== 'github' && (
                        <p className="hint-text">
                          Set a GitHub username to grant repository access.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Notes, Tags & Password Expiry */}
                <div className="user-details-section">
                  <h4>Admin notes & metadata</h4>
                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-input"
                      rows={3}
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Internal notes about this user..."
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tags (comma-separated)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editTags}
                      onChange={(e) => setEditTags(e.target.value)}
                      placeholder="contractor, team-alpha"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Password expiry date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={editPasswordExpiry}
                      onChange={(e) => setEditPasswordExpiry(e.target.value)}
                    />
                  </div>
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={savingUserMeta}
                    onClick={() => handleSaveUserMeta(selectedUserDetails._id)}
                  >
                    {savingUserMeta ? 'Saving…' : 'Save notes & metadata'}
                  </button>
                </div>

                {/* Recent Activity */}
                <div className="user-details-section">
                  <h4>Recent activity</h4>
                  {loadingActivity ? (
                    <div className="activity-loading">Loading…</div>
                  ) : userActivityLogs.length === 0 ? (
                    <p className="empty-activity">No recent activity</p>
                  ) : (
                    <ul className="activity-timeline">
                      {userActivityLogs.slice(0, 15).map((log, idx) => (
                        <li key={log._id || idx} className="activity-item">
                          <span className="activity-action">{log.action?.replace(/_/g, ' ') || 'Activity'}</span>
                          {(log.adminUsername || log.adminId?.username) && (log.adminUsername || log.adminId?.username) !== selectedUserDetails?.username && (
                            <span className="activity-by">by {log.adminUsername || log.adminId?.username}</span>
                          )}
                          <span className="activity-time">{log.createdAt ? formatDate(log.createdAt) : ''}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="user-details-actions">
                  <button
                    className="btn btn-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowRepoAccess(selectedUserDetails._id);
                      if (!userRepos[selectedUserDetails._id]) {
                        fetchUserRepos(selectedUserDetails._id);
                      }
                      setSelectedUserDetails(null);
                    }}
                  >
                    Manage Repository Access
                  </button>
                  {!selectedUserDetails.githubUsername && selectedUserDetails.accountType !== 'github' && (
                    <button
                      className="btn btn-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSetGitHubUsername(selectedUserDetails._id);
                        setGithubUsernameData({ githubUsername: '' });
                        setSelectedUserDetails(null);
                      }}
                    >
                      Set GitHub Username
                    </button>
                  )}
                  <button
                    className="btn btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowResetPasswordForm(selectedUserDetails._id);
                      setSelectedUserDetails(null);
                    }}
                  >
                    Reset Password
                  </button>
                  {selectedUserDetails.mfaEnabled && (
                    <button
                      className="btn btn-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Disable MFA for this user?')) {
                          handleDisableMFA(selectedUserDetails._id);
                          setSelectedUserDetails(null);
                        }
                      }}
                    >
                      Disable MFA
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter Preset Save Modal */}
      {showPresetModal && (
        <div className="modal-overlay" onClick={() => setShowPresetModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Save Filter Preset</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowPresetModal(false);
                  setPresetName('');
                }}
              >
                ×
              </button>
            </div>
            <div className="form-group">
              <label className="form-label">Preset Name</label>
              <input
                type="text"
                className="form-input"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="e.g., Active Admins, Recent Users"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSavePreset();
                  }
                }}
              />
              <p className="form-hint">Save current filters and search settings for quick access</p>
            </div>
            <div className="form-actions">
              <button
                className="btn btn-primary"
                onClick={handleSavePreset}
              >
                Save Preset
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowPresetModal(false);
                  setPresetName('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Import Users from CSV</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportPreview([]);
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleImportCSV}>
              <div className="form-group">
                <label className="form-label">CSV File</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="form-input"
                  required
                />
                <p className="form-hint">
                  CSV format: username, email, password, displayName, role, githubUsername
                </p>
              </div>

              {importPreview.length > 0 && (
                <div className="import-preview">
                  <h4>Preview (first 5 rows):</h4>
                  <div className="preview-table">
                    {importPreview.map((line, idx) => (
                      <div key={idx} className="preview-row">{line}</div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!importFile || importing}
                >
                  {importing ? 'Importing...' : 'Import Users'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                    setImportPreview([]);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Pagination */}
      {users.length > 0 && (
        <div className="pagination-section">
          <div className="pagination-info">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, users.length)} of {users.length} users
          </div>
          <div className="pagination-controls">
            <select
              className="form-select pagination-select"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <div className="pagination-buttons">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                ««
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                «
              </button>
              <span className="pagination-page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                »
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                »»
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
